import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { isPushSupported, subscribeToPush } from '@/lib/push-permission';

interface NotificationsState {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
}

export function useNotifications(): {
  state: NotificationsState;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  sendTest: () => Promise<void>;
} {
  const [state, setState] = useState<NotificationsState>({
    supported: false,
    permission: 'default',
    subscribed: false,
  });

  useEffect(() => {
    if (!isPushSupported()) {
      setState((s) => ({ ...s, supported: false }));
      return;
    }
    setState((s) => ({ ...s, supported: true, permission: Notification.permission }));
    void navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setState((s) => ({ ...s, subscribed: !!sub }));
    });
  }, []);

  const subscribe = useCallback(async () => {
    const result = await subscribeToPush();
    if (result === 'unsupported') throw new Error('Push não suportado neste navegador');
    setState((s) => ({
      ...s,
      permission: result === 'granted' ? 'granted' : 'denied',
      subscribed: result === 'granted',
    }));
  }, []);

  const unsubscribe = useCallback(async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.delete('notifications/subscribe', {
        headers: { 'x-endpoint': sub.endpoint },
      });
      await sub.unsubscribe();
    }
    setState((s) => ({ ...s, subscribed: false }));
  }, []);

  const sendTest = useCallback(async () => {
    await api.post('notifications/test');
  }, []);

  return { state, subscribe, unsubscribe, sendTest };
}
